import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type BoundaryKind,
  type DetectedStep,
  type JourneyEvidenceResponse,
  type JourneyEvidenceRow,
  type JourneyStatus,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import { boundaryPhrase, journeyTypeLabel } from './journeyLabels';
import styles from './JourneyEvidence.module.css';

type LoadState = 'loading' | 'error' | 'ready';

export interface JourneyEvidenceProps {
  client?: ApiClient;
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

type InWindowRow = Extract<JourneyEvidenceRow, { expired: false }>;

function isInWindow(row: JourneyEvidenceRow): row is InWindowRow {
  return row.expired === false;
}

/** A row stops at a transaction boundary that needs configuration to validate
 * further (payment / account / uncertain). Read-only and reversible pages are
 * freely discoverable, so they never require configuration. */
function requiresConfiguration(kind: BoundaryKind | null): boolean {
  return kind === 'financial' || kind === 'state-changing' || kind === 'uncertain';
}

/** Header status derived from IN-WINDOW rows only. Null when every row has
 * expired — we never assert a discovery status we can no longer prove. */
function headerStatus(rows: InWindowRow[]): JourneyStatus | null {
  if (rows.length === 0) return null;
  return rows.some((row) => requiresConfiguration(row.reasoning.boundaryKind))
    ? 'configuration_required'
    : 'discovered';
}

/**
 * Project journey discovery-evidence drill-down (#41a). Deep-linkable view of the
 * crawled pages and classifier reasoning that discovered ONE journey type. This
 * surface is DISCOVERY-EVIDENCE ONLY — the DTO's closed `state` set makes an
 * execution/run/validated outcome structurally unrepresentable. Expired evidence
 * shows an honest "outside the retention window" placeholder (never "deleted",
 * never an error, never masked fields). A manual Refresh re-fetches (no polling);
 * async results announce in a polite live region.
 */
export function JourneyEvidence({ client = defaultClient }: JourneyEvidenceProps) {
  const { currentOrg } = useOrg();
  const { id, journeyType } = useParams<{ id: string; journeyType: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;
  const type = journeyType ?? '';

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [data, setData] = useState<JourneyEvidenceResponse | null>(null);
  const [steps, setSteps] = useState<DetectedStep[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!orgId || !id || !type) return;
    setPhase('loading');
    // The detected flow is supplementary — a steps failure never breaks the page.
    const [evidenceResult, flowResult] = await Promise.allSettled([
      client.journeyEvidence(orgId, id, type),
      client.journeyDetectedSteps(orgId, id, type),
    ]);
    if (evidenceResult.status === 'rejected') {
      setLoadMessage(toDisplayError(evidenceResult.reason).message);
      setPhase('error');
      return;
    }
    setData(evidenceResult.value);
    setSteps(flowResult.status === 'fulfilled' ? flowResult.value.steps : []);
    setPhase('ready');
  }, [client, orgId, id, type]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    if (!orgId || !id || !type || refreshing) return;
    setRefreshing(true);
    const [evidenceResult, flowResult] = await Promise.allSettled([
      client.journeyEvidence(orgId, id, type),
      client.journeyDetectedSteps(orgId, id, type),
    ]);
    if (evidenceResult.status === 'rejected') {
      setLoadMessage(toDisplayError(evidenceResult.reason).message);
      setPhase('error');
      setRefreshing(false);
      return;
    }
    setData(evidenceResult.value);
    setSteps(flowResult.status === 'fulfilled' ? flowResult.value.steps : []);
    setStatusMessage('Discovery evidence updated.');
    setRefreshing(false);
  }, [client, orgId, id, type, refreshing]);

  const label = journeyTypeLabel(type);
  const inWindow = data ? data.observations.filter(isInWindow) : [];
  const badge = headerStatus(inWindow);
  const backHref = `/projects/${id ?? ''}/journey-health`;

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <p className={styles.back}>
          <Link to={backHref}>Back to journey health</Link>
        </p>

        <div className={styles.titleRow}>
          <h1 id="je-heading" className={styles.heading}>
            {label} — discovery evidence
          </h1>
          {phase === 'ready' && badge ? <StatusBadge kind="journey" status={badge} /> : null}
        </div>

        <p className={styles.subheading}>
          The crawled pages and classification reasoning that discovered this journey. Discovery
          evidence only — this journey was found by crawling; it has not been carried out or checked
          for an outcome.
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading discovery evidence…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' && data ? (
          <>
            <InlineAlert severity="success">{statusMessage}</InlineAlert>

            <div className={styles.actions}>
              <Button variant="secondary" busy={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>

            {data.state === 'no-data' ? (
              <div className={styles.panel}>
                <InlineAlert severity="info">
                  No scans have completed yet — run a scan to discover journeys.{' '}
                  <Link to={`/projects/${id ?? ''}/scans`}>Go to scans</Link>
                </InlineAlert>
              </div>
            ) : null}

            {data.state === 'expired' ? (
              <div className={styles.panel}>
                <InlineAlert severity="warning">
                  Evidence expired — outside the retention window. The pages that discovered this
                  journey were observed more than {data.windowDays} days ago, so their evidence is
                  outside your retention window and no longer available.
                </InlineAlert>
              </div>
            ) : null}

            {data.state === 'ok' ? (
              <>
                <div className={styles.panel}>
                  <InlineAlert severity="info">
                    <strong>What you&rsquo;re seeing.</strong> This is how TestCraft found the
                    journey &mdash; the pages it crawled and why it grouped them together. It was
                    mapped, not carried out, so there&rsquo;s no pass/fail here yet. Each card below
                    is one page TestCraft looked at: how deep it sits (clicks from the homepage), its
                    response, the page&rsquo;s structure (the values you&rsquo;d type are never
                    captured), and why the classifier linked it to this journey &mdash; up to the
                    point a shopper would sign in, create an account, or pay.
                  </InlineAlert>
                </div>
                {steps.length > 0 ? (
                  <section className={styles.flow} aria-labelledby="je-flow-heading">
                    <h2 id="je-flow-heading" className={styles.flowHeading}>
                      Detected flow
                    </h2>
                    <p className={styles.flowHelp}>
                      The ordered path TestCraft pieced together from the crawl. A step marked{' '}
                      <em>inferred</em> was ordered by page depth rather than a directly observed
                      link; a <em>tentative</em> step is below the confidence threshold.
                    </p>
                    <ol className={styles.flowList}>
                      {steps.map((step) => (
                        <li key={step.stepOrder} className={styles.flowStep}>
                          <span className={styles.flowOrder}>{step.stepOrder}</span>
                          <span className={styles.flowLabel}>{step.description}</span>
                          {step.orderingInferred ? (
                            <span className={styles.mutedBadge}>inferred</span>
                          ) : null}
                          {step.tentative ? (
                            <span className={styles.mutedBadge}>tentative</span>
                          ) : null}
                          <span className={styles.flowConfidence}>
                            {Math.round(step.confidence * 100)}% confidence
                          </span>
                        </li>
                      ))}
                    </ol>
                  </section>
                ) : null}
                <ul className={styles.list} aria-labelledby="je-heading">
                  {data.observations.map((row) => (
                    <li key={row.observationId}>
                      {isInWindow(row) ? (
                        <EvidenceCard row={row} />
                      ) : (
                        <ExpiredCard observationId={row.observationId} expiredAsOf={row.expiredAsOf} />
                      )}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}

function ExpiredCard({
  observationId,
  expiredAsOf,
}: {
  observationId: string;
  expiredAsOf: string;
}) {
  const headingId = `obs-${observationId}-expired`;
  const asOf = formatTime(expiredAsOf);
  return (
    <article className={styles.card} aria-labelledby={headingId}>
      <p id={headingId} className={styles.expired}>
        This observation is outside the retention window, so its evidence is no longer available.
      </p>
      {asOf ? <p className={styles.metaMuted}>Aged out {asOf}</p> : null}
    </article>
  );
}

function EvidenceCard({ row }: { row: InWindowRow }) {
  const headingId = `obs-${row.observationId}-url`;
  const scanned = formatTime(row.scanCompletedAt);
  const rules = row.reasoning.matchedRules;
  return (
    <article className={styles.card} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.cardHeading}>
        {row.url}
      </h2>

      <p className={styles.meta}>
        <span>Depth {row.depth}</span>
        <span aria-hidden="true">·</span>
        <span>
          {row.httpMethod} · {row.statusCode ?? '—'}
        </span>
        {scanned ? (
          <>
            <span aria-hidden="true">·</span>
            <span>{scanned}</span>
          </>
        ) : null}
      </p>

      {row.robotsExcluded || row.outOfScope ? (
        <p className={styles.badgeRow}>
          {row.robotsExcluded ? <span className={styles.mutedBadge}>robots-excluded</span> : null}
          {row.outOfScope ? <span className={styles.mutedBadge}>out of scope</span> : null}
        </p>
      ) : null}

      <div className={styles.section}>
        <h3 className={styles.sectionHeading}>Redirects</h3>
        {row.redirectChain.length > 0 ? (
          <ol className={styles.redirects}>
            {row.redirectChain.map((url, i) => (
              <li key={`${row.observationId}-redirect-${i}`}>{url}</li>
            ))}
          </ol>
        ) : (
          <p className={styles.metaMuted}>No redirects</p>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionHeading}>
          Page structure (metadata only — no entered values captured)
        </h3>
        {row.forms.length > 0 ? (
          <ul className={styles.forms}>
            {row.forms.map((form, fi) => (
              <li key={`${row.observationId}-form-${fi}`}>
                <span className={styles.formMethod}>{form.method.toUpperCase()} form</span>
                {form.fields.length > 0 ? (
                  <ul className={styles.fields}>
                    {form.fields.map((field, fx) => (
                      <li key={`${row.observationId}-form-${fi}-field-${fx}`}>
                        {field.name ?? '(unnamed)'} · {field.type ?? 'text'}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.metaMuted}>No fields</p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.metaMuted}>No forms on this page</p>
        )}
        <p className={styles.counts}>
          Links: {row.linkCount} · Buttons: {row.buttonCount}
        </p>
        {row.buttonNames.length > 0 ? (
          <ul className={styles.chips}>
            {row.buttonNames.map((name, bi) => (
              <li key={`${row.observationId}-btn-${bi}`} className={styles.chip}>
                {name}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionHeading}>Classification reasoning</h3>
        <p>Reached {boundaryPhrase(row.reasoning.boundaryKind)}.</p>
        <p>Confidence {Math.round(row.reasoning.confidence * 100)}%</p>
        {rules.length > 0 ? (
          <ul className={styles.chips}>
            {rules.map((rule, ri) => (
              <li key={`${row.observationId}-rule-${ri}`} className={styles.chip}>
                {rule}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </article>
  );
}
